import AgoraRTC, {IAgoraRTCClient, IAgoraRTCRemoteUser} from "agora-rtc-sdk-ng";
import {AgoraClientModes as AgoraClientModesEnum} from "@share/web-conferences/enums/AgoraClientModes";
import {AgoraCodecs} from "@share/web-conferences/enums/AgoraCodecs";
import {AgoraUserEvents} from "@share/web-conferences/enums/AgoraUserEvents";
import {AgoraMediaType} from "@share/web-conferences/types/AgoraMediaType";

export class AgoraTestClient {
    client: IAgoraRTCClient | null = null;
    networkQualities: number[] = [];
    averageQuality = -1;
    networkQualityTimerId = 0;
    isJoining = false;
    joinTimerId = 0;

    constructor(private readonly type: 'downlink' | 'uplink') {
        this.client = AgoraRTC.createClient({
            mode: AgoraClientModesEnum.Rtc,
            codec: AgoraCodecs.Vp8,
            role: 'host'
        });
    }

    /**
     * Subscribe to network-quality event to gather the statistics
     * Statistics is being received until the length of the networkQualities array become 5 or the networkQualityTimerId expires
     */
    initQualityNetworkListener() {
        this.startNetworkQualityTimer();
        this.client?.on("network-quality", (data) => {
            this.networkQualities.push(this.type == 'downlink' ? data.downlinkNetworkQuality : data.uplinkNetworkQuality);
            if (this.networkQualities.length >= 5) {
                this.finalize();
            }
        });
    }

    /**
     * Calculate average quality and remove quality listener
     * @private
     */
    private finalize() {
        this.client?.removeAllListeners("network-quality");
        this.calcAverageQuality();
    }

    /**
     * Calculates average quality
     * @private
     */
    private calcAverageQuality() {
        if (this.networkQualities.length == 0) {return 6;}

        this.averageQuality = Math.round(this.networkQualities.reduce((accum, item) => accum + item, 0) / this.networkQualities.length);
    }

    /**
     * Join the agora client
     * @param appId
     * @param channelId
     * @param token
     * @param userId
     */
    async join(appId: string, channelId: string, token: string, userId: string) {
        try {
            this.isJoining = true;
            this.startJoinTimeout();
            await this.client?.join(appId, channelId, token, userId);
            this.stopJoining();
        }
        catch (e) {
            this.stopJoining();
            await this.clean();
            throw e;
        }
    }

    private startJoinTimeout() {
        this.joinTimerId = window.setTimeout(async () => {
            if (this.isJoining) {
                await this.client?.leave();
                this.isJoining = false;
            }
        }, 10000)
    }

    private stopJoining() {
        this.isJoining = false;
        clearTimeout(this.joinTimerId);
    }

    attachUserPublishedEvent() {
        this.client?.on(AgoraUserEvents.UserPublished, (user: IAgoraRTCRemoteUser, mediaType: AgoraMediaType) => {
            this.client?.subscribe(user, mediaType)
        });
    }

    private startNetworkQualityTimer() {
        this.networkQualityTimerId = window.setTimeout(() => {
            this.finalize();
        }, 15000);
    }

    private stopNetworkQualityTimer() {
        clearTimeout(this.networkQualityTimerId);
    }

    async clean() {
        await this.client?.leave();
        await this.client?.removeAllListeners();
        this.networkQualities = [];
        this.stopNetworkQualityTimer();
    }
}
